import { type ReactElement } from 'react';
import { useTable, List, ShowButton, EditButton } from '@refinedev/antd';
import type { HttpError, CrudFilters } from '@refinedev/core';
import {
  Table,
  Tag,
  Space,
  Badge,
  Input,
  Form,
  List as AntList,
  Card,
  Grid,
} from 'antd';

import type { AdminCustomer } from '@/types';

const { useBreakpoint } = Grid;

// ---------------------------------------------------------------------------
// Search form shape
// ---------------------------------------------------------------------------

interface SearchForm {
  q: string;
}

// ---------------------------------------------------------------------------
// Mobile card component
// ---------------------------------------------------------------------------

interface MobileCustomerCardProps {
  record: AdminCustomer;
}

function MobileCustomerCard({ record }: MobileCustomerCardProps): ReactElement {
  return (
    <Card size="small" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div>
          <strong>{record.email}</strong>
        </div>
        <div>
          <strong>Name:</strong> {record.name ?? '—'}
        </div>
        <div>
          <strong>Country:</strong> {record.country ?? '—'}
        </div>
        <div>
          <strong>Orders:</strong> <Badge count={record.orderCount} showZero />
        </div>
        <div>
          <strong>Marketing:</strong>{' '}
          {record.marketingOptIn ? (
            <Tag color="green">Opt-in</Tag>
          ) : (
            <Tag color="red">Opted-out</Tag>
          )}
        </div>
        <Space size="small">
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      </Space>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomerList(): ReactElement {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { tableProps, searchFormProps } = useTable<
    AdminCustomer,
    HttpError,
    SearchForm
  >({
    resource: 'customers',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    onSearch: (values) => {
      const filters: CrudFilters = [
        {
          field: 'q',
          operator: 'contains',
          value: values.q || undefined,
        },
      ];
      return filters;
    },
  });

  return (
    <List>
      <Space style={{ marginBottom: 16 }}>
        <Form {...searchFormProps} layout="inline">
          <Form.Item name="q">
            <Input.Search
              placeholder="Search email / name"
              allowClear
              onSearch={(value) => {
                searchFormProps.form?.setFieldsValue({ q: value });
                searchFormProps.form?.submit();
              }}
              style={{ width: 280 }}
            />
          </Form.Item>
        </Form>
      </Space>

      {isMobile ? (
        <AntList
          dataSource={tableProps.dataSource ? [...tableProps.dataSource] : []}
          pagination={
            tableProps.pagination && typeof tableProps.pagination === 'object'
              ? {
                  current: tableProps.pagination.current,
                  pageSize: tableProps.pagination.pageSize,
                  total: tableProps.pagination.total,
                  onChange: tableProps.pagination.onChange,
                }
              : false
          }
          renderItem={(record) => (
            <AntList.Item key={record.id} style={{ padding: '8px 0' }}>
              <MobileCustomerCard record={record} />
            </AntList.Item>
          )}
        />
      ) : null}

      {!isMobile ? (
        <Table {...tableProps} rowKey="id" scroll={{ x: 900 }} size="small">
          <Table.Column<AdminCustomer>
            dataIndex="email"
            title="Email"
            ellipsis
            width={220}
          />

          <Table.Column<AdminCustomer>
            dataIndex="name"
            title="Name"
            render={(value: string | null) => value ?? '—'}
            width={160}
          />

          <Table.Column<AdminCustomer>
            dataIndex="country"
            title="Country"
            render={(value: string | null) => value ?? '—'}
            width={100}
          />

          <Table.Column<AdminCustomer>
            dataIndex="orderCount"
            title="Orders"
            render={(value: number) => <Badge count={value} showZero />}
            width={90}
            align="center"
          />

          <Table.Column<AdminCustomer>
            dataIndex="marketingOptIn"
            title="Marketing"
            render={(value: boolean) =>
              value ? <Tag color="green">Opt-in</Tag> : <Tag>Opted-out</Tag>
            }
            width={110}
          />

          <Table.Column<AdminCustomer>
            dataIndex="createdAt"
            title="Joined"
            render={(value: string) => new Date(value).toLocaleDateString()}
            width={120}
          />

          <Table.Column<AdminCustomer>
            title="Actions"
            fixed="right"
            width={100}
            render={(_: unknown, record: AdminCustomer) => (
              <Space size="small">
                <EditButton hideText size="small" recordItemId={record.id} />
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        </Table>
      ) : null}
    </List>
  );
}
